/**
 * Système de logging centralisé avec support Sentry
 */

import React from 'react';

export interface LogContext {
  userId?: string;
  companyId?: string;
  context?: string;
  [key: string]: any;
}

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: any;
  context?: LogContext;
}

class Logger {
  private isDevelopment = (import.meta as any).env.DEV;
  private logs: LogEntry[] = [];
  private maxLogs = 100;
  private sentryEnabled = false;

  /**
   * Initialiser Sentry (optionnel)
   */
  initSentry(dsn: string, environment: string = 'production') {
    // Lazy load Sentry pour garder bundle léger si non utilisé
    import('@sentry/react').then((Sentry) => {
      Sentry.init({
        dsn,
        environment,
        tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
        debug: this.isDevelopment,
        beforeSend: (event) => {
          // Filtrer les erreurs non critiques en production
          if (environment === 'production' && event.level === 'info') {
            return null;
          }
          return event;
        },
      });
      this.sentryEnabled = true;
      this.info('[Logger] Sentry initialized', { environment });
    });
  }

  /**
   * Ajouter contexte utilisateur pour tracking
   */
  setUser(userId: string, email?: string, companyId?: string) {
    if (this.sentryEnabled) {
      import('@sentry/react').then((Sentry) => {
        Sentry.setUser({ id: userId, email, companyId });
      });
    }
  }

  /**
   * Effacer contexte utilisateur (logout)
   */
  clearUser() {
    if (this.sentryEnabled) {
      import('@sentry/react').then((Sentry) => {
        Sentry.setUser(null);
      });
    }
  }

  /**
   * Log interne
   */
  private addLog(level: LogLevel, message: string, data?: any, context?: LogContext) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
      context,
    };

    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Console en dev
    if (this.isDevelopment) {
      const prefix = `[${level.toUpperCase()}]`;
      const style = {
        debug: 'color: #888',
        info: 'color: #0070f3',
        warn: 'color: #ff922b',
        error: 'color: #ff0000',
      };

      if (data) {
        console[level === 'warn' ? 'warn' : level === 'error' ? 'error' : 'log'](
          `%c${prefix} ${message}`,
          style[level],
          data
        );
      } else {
        console[level === 'warn' ? 'warn' : level === 'error' ? 'error' : 'log'](
          `%c${prefix} ${message}`,
          style[level]
        );
      }
    }
  }

  /**
   * Niveau DEBUG
   */
  debug(message: string, data?: any, context?: LogContext) {
    this.addLog('debug', message, data, context);
  }

  /**
   * Niveau INFO
   */
  info(message: string, data?: any, context?: LogContext) {
    this.addLog('info', message, data, context);
  }

  /**
   * Niveau WARNING
   */
  warn(message: string, data?: any, context?: LogContext) {
    this.addLog('warn', message, data, context);

    if (this.sentryEnabled) {
      import('@sentry/react').then((Sentry) => {
        Sentry.captureMessage(message, 'warning');
      });
    }
  }

  /**
   * Niveau ERROR
   */
  error(message: string, error?: Error | any, context?: LogContext) {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    this.addLog('error', message, { error: errorObj.message, stack: errorObj.stack }, context);

    if (this.sentryEnabled) {
      import('@sentry/react').then((Sentry) => {
        Sentry.captureException(errorObj, {
          contexts: context,
          extra: {
            message,
          },
        });
      });
    }
  }

  /**
   * Capturer exception
   */
  captureException(error: Error, context?: LogContext) {
    this.error(error.message, error, context);
  }

  /**
   * Exporter logs pour debugging
   */
  exportLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * Envoyer logs au serveur (pour persistance)
   */
  async flush() {
    if (this.sentryEnabled) {
      import('@sentry/react').then((Sentry) => {
        Sentry.flush(2000);
      });
    }
  }
}

// Export singleton
export const logger = new Logger();

/**
 * Error boundary component pour React
 */
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    logger.error('React Error Boundary', error, {
      context: 'ErrorBoundary',
      componentStack: errorInfo.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="fixed inset-0 flex items-center justify-center bg-slate-900/50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md border border-rose-200 dark:border-rose-900">
              <h2 className="text-xl font-bold text-rose-600 mb-2">Oups! Une erreur est survenue</h2>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                {(import.meta as any).env.DEV
                  ? this.state.error?.message
                  : 'Une erreur inattendue s\'est produite. Veuillez réessayer.'}
              </p>
              <button type="button"
                onClick={() => window.location.reload()}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Recharger la page
              </button>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
